import pWaitFor, { TimeoutError } from 'p-wait-for';
import React from 'react';
import type { FC } from 'react';
import {
  Alert,
  Badge,
  Box,
  Link,
  Spinner,
  Text,
  useAsync,
} from '@wix/cli-ui-kit';
import { isType, match, variant, fields } from 'variant';
import { getTestOverrides } from '@wix/cli-test-overrides';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { useErrorReporter } from '@wix/cli-error-reporting';
import { openBrowser } from '@wix/user-browser';
import {
  gitSetRemoteUrl,
  gitPush,
  isInsideGitRepo,
  gitGetUserInfo,
} from '@wix/package-manager';
import type { GitHubRepoInfo } from '@wix/velo-client';
import { useVeloClient } from '@wix/velo-client';
import { useCodestoreClient } from '@wix/codestore-client';
import { writeCrashReport } from '@wix/cli-debug-log';
import { getDebugLogFilePath } from '@wix/cli-core-definitions';

interface Props {
  projectId: string;
  projectFolder: string;
  onSubmit?: () => void;
}

const VibeSetupResult = variant({
  Success: fields<GitHubRepoInfo>(),
  Canceled: {},
  Failed: {},
});

function isAbortError(error: unknown) {
  return (
    error &&
    typeof error === 'object' &&
    'name' in error &&
    error.name === 'AbortError'
  );
}

export const VibeRepositorySetupPolling: FC<Props> = ({
  projectId,
  projectFolder,
  onSubmit,
}) => {
  const { getGitHubOnboardingStatus, initGitHubRepository } = useVeloClient();
  const { reportError } = useErrorReporter();
  const { createProject } = useCodestoreClient();

  const { status } = useAsync(
    async ({ signal }) => {
      const { pollInterval } = getTestOverrides();

      const vibeSetupUrl = `https://manage.wix.com/_api/wix-headless-cli-github-integration-serverless?projectId=${projectId}`;
      await openBrowser(vibeSetupUrl);

      try {
        const repoInfo = await pWaitFor(
          async () => {
            const result = await getGitHubOnboardingStatus(projectId);
            if (result) {
              return pWaitFor.resolveWith(result);
            }
            return false;
          },
          {
            interval: pollInterval ?? 1000,
            timeout: {
              milliseconds: 60 * 1000 * 5,
              signal,
            },
          }
        );

        const isGitRepo = await isInsideGitRepo(projectFolder);

        if (isGitRepo) {
          const currentUserInfo = await gitGetUserInfo(projectFolder);

          await createProject(projectId, currentUserInfo);

          await initGitHubRepository(projectId);
          await gitSetRemoteUrl(projectFolder, 'origin', repoInfo.htmlUrl);
          await gitPush(projectFolder, 'origin', { force: true });
        }

        return VibeSetupResult.Success(repoInfo);
      } catch (error) {
        if (error instanceof TimeoutError) {
          throw new CliError({
            code: CliErrorCode.VibeRepositorySetupTimedout(),
            cause: error,
          });
        }

        if (isAbortError(error)) {
          return VibeSetupResult.Canceled();
        }

        throw error;
      }
    },
    [projectFolder, projectId],
    {
      onSuccess: (result) => {
        if (isType(result, VibeSetupResult.Success)) {
          onSubmit?.();
        }
      },
      onError: (error) => {
        reportError(error);
        writeCrashReport(getDebugLogFilePath(projectFolder), error, new Date());
        return VibeSetupResult.Failed();
      },
    }
  );

  return match(status, {
    Error: () => (
      <Box flexDirection="column" gap={1}>
        <Text skin="error">Failed to connect to Wix Vibe</Text>
      </Box>
    ),
    Loading: () => (
      <Box flexDirection="column" gap={1}>
        <Spinner text="Opening browser for Wix Vibe setup..." />
        <Text skin="secondary">
          Complete the setup in your browser. Waiting for you to finish...
        </Text>
      </Box>
    ),
    Success: ({ result }) =>
      match(result, {
        Success: ({ repoName, htmlUrl }) => (
          <Box marginLeft={-2} marginBottom={1}>
            <Alert type="success">
              <Badge skin="success">Success</Badge>{' '}
              <Text>
                GitHub repository '{repoName}' created and Synced.{' '}
                <Link url={htmlUrl}>View Repository.</Link>
              </Text>
            </Alert>
          </Box>
        ),
        Canceled: () => null,
      }),
  });
};
